import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { newRegister, verifyOtp, resendOtp, clearErrors } from '../../actions/authActions';
import AuthCommonBanner from './AuthCommonBanner';

// Minimal, self-contained styles compatible with existing app
const Field = ({label, children}) => (
  <div className="form-group">
    <label className="control-label">{label}</label>
    {children}
  </div>
);

const RegisterNew = ({ history }) => {
  const dispatch = useDispatch();
  const auth = useSelector((s) => s.auth || {});

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile_code: '234',
    mobile_number: '',
    password: '',
    confirm_password: '',
    agree: false,
  });
  const [otp, setOtp] = useState(['','','','','','']);
  const [localError, setLocalError] = useState(null);
  const [resendCountdown, setResendCountdown] = useState(0); // seconds

  // Clear any residual auth errors on mount so old messages like
  // "Unauthorised access" don't show on the fresh register page
  useEffect(() => {
    dispatch(clearErrors());
  }, [dispatch]);

  useEffect(() => {
    if (auth.error) setLocalError(auth.error);
    else setLocalError(null);
  }, [auth.error]);

  useEffect(() => {
    if (auth.isRegistered && auth.pendingOtpEmail && step === 1) {
      setStep(2);
    }
  }, [auth.isRegistered, auth.pendingOtpEmail, step]);

  // When OTP step shows for the first time, start a 3-minute cooldown
  useEffect(() => {
    if (step === 2 && resendCountdown === 0) {
      setResendCountdown(180);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Tick down the resend cooldown timer
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setInterval(() => setResendCountdown((s) => s - 1), 1000);
    return () => clearInterval(timer);
  }, [resendCountdown]);

  useEffect(() => {
    if (auth.otpVerified) {
      // After verification, redirect to login
      setTimeout(() => history.push('/login'), 800);
    }
  }, [auth.otpVerified, history]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const validate = () => {
    if (!form.first_name.trim() || !/^[A-Za-z\s]+$/.test(form.first_name)) return 'First name must contain only letters';
    if (!form.last_name.trim() || !/^[A-Za-z\s]+$/.test(form.last_name)) return 'Last name must contain only letters';
    if (!/^([^\s@]+)@([^\s@]+)\.[^\s@]+$/.test(form.email)) return 'Enter a valid email address';
    if (form.mobile_number.trim() && !/^\d{7,15}$/.test(form.mobile_number.trim())) return 'Enter a valid phone number (digits only)';
    if (form.password.length < 8) return 'Password must be at least 8 characters';
    if (form.password !== form.confirm_password) return 'Passwords do not match';
    if (!form.agree) return 'You must accept the terms & privacy policy';
    return null;
  };

  const submitStep1 = (e) => {
    e.preventDefault();
    setLocalError(null);
    dispatch(clearErrors());
    const v = validate();
    if (v) return setLocalError(v);
    dispatch(newRegister(form.first_name, form.last_name, form.email, form.mobile_code, form.mobile_number, form.password, form.confirm_password));
  };

  const submitVerify = (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length !== 6) return setLocalError('Please enter the 6-digit code');
    setLocalError(null);
    dispatch(verifyOtp(auth.pendingOtpEmail || form.email, code));
  };

  const handleResend = () => {
    // Immediately start cooldown to prevent spamming
    if (resendCountdown <= 0) setResendCountdown(180);
    dispatch(resendOtp(auth.pendingOtpEmail || form.email));
  };

  const mm = Math.floor(resendCountdown / 60)
    .toString()
    .padStart(1, '0');
  const ss = (resendCountdown % 60).toString().padStart(2, '0');

  return (
    <section className="registration-page clearfix fullHt">
      <div className="right-regi-step login-page">
        <div className="vh-center">
          <h1>Sign Up</h1>
          {step === 1 && (
            <form onSubmit={submitStep1} className="inner-que">
              {/* Only show validation errors; suppress generic stale backend text like 'Unauthorised access' */}
              {localError && localError !== 'Unauthorised access' && (
                <div className="alert alert-danger" role="alert">{localError}</div>
              )}
              {auth.registrationMessage && <div className="alert alert-success" role="alert">{auth.registrationMessage}</div>}
              <div className="row">
                <div className="col-sm-6"><Field label="First Name *">
                  <input name="first_name" value={form.first_name} onChange={onChange} className="form-control" />
                </Field></div>
                <div className="col-sm-6"><Field label="Last Name *">
                  <input name="last_name" value={form.last_name} onChange={onChange} className="form-control" />
                </Field></div>
              </div>
              <div className="row">
                <div className="col-sm-12"><Field label="Email *">
                  <input type="email" name="email" value={form.email} onChange={onChange} className="form-control" />
                </Field></div>
              </div>
              <div className="row">
                <div className="col-sm-3"><Field label="Code">
                  <select name="mobile_code" value={form.mobile_code} onChange={onChange} className="form-control">
                    <option value="234">+234 (NG)</option>
                    <option value="1">+1 (US)</option>
                    <option value="44">+44 (UK)</option>
                    <option value="91">+91 (IN)</option>
                    <option value="233">+233 (GH)</option>
                    <option value="254">+254 (KE)</option>
                    <option value="27">+27 (ZA)</option>
                  </select>
                </Field></div>
                <div className="col-sm-9"><Field label="Phone Number">
                  <input type="tel" name="mobile_number" value={form.mobile_number} onChange={onChange} className="form-control" placeholder="e.g. 08012345678" />
                </Field></div>
              </div>
              <div className="row">
                <div className="col-sm-6"><Field label="Password *">
                  <input type="password" name="password" value={form.password} onChange={onChange} className="form-control" />
                </Field></div>
                <div className="col-sm-6"><Field label="Confirm Password *">
                  <input type="password" name="confirm_password" value={form.confirm_password} onChange={onChange} className="form-control" />
                </Field></div>
              </div>
              <div className="checkbox">
                <label>
                  <input type="checkbox" name="agree" checked={form.agree} onChange={onChange} /> By registering, you agree to our <a href="/pages/terms-conditions" target="_blank" rel="noreferrer">Terms</a> and <a href="/pages/privacy-policy" target="_blank" rel="noreferrer">Privacy</a>.
                </label>
              </div>
              <ul className="log-btn-main clearfix">
                <li className="pull-left dont-have-ac">Back to login? <a href="/login">Log In</a></li>
                <li className="pull-right">
                  <button type="submit" className="btn btn-primary" disabled={auth.loading}>Sign Up</button>
                </li>
              </ul>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={submitVerify} className="inner-que">
              {auth.otpError && <div className="alert alert-danger" role="alert">{auth.otpError}</div>}
              {auth.otpMessage && <div className="alert alert-success" role="alert">{auth.otpMessage}</div>}
              <p>We've sent a 6-digit verification code to <strong>{auth.pendingOtpEmail || form.email}</strong></p>
              <div className="row">
                <div className="col-sm-12" style={{display:'flex', gap:8}}>
                  {otp.map((d,i)=> (
                    <input key={i} maxLength={1} className="form-control" style={{width:50, textAlign:'center'}} value={d}
                      onChange={(e)=>{
                        const v=e.target.value.replace(/\D/g,'');
                        const next=[...otp]; next[i]=v; setOtp(next);
                      }} />
                  ))}
                </div>
              </div>
              <div className="row" style={{marginTop:12}}>
                <div className="col-sm-12">
                  <button type="submit" className="btn btn-primary" disabled={auth.otpVerifying}>Verify Code</button>
                  <button
                    type="button"
                    className="btn btn-default"
                    style={{marginLeft:8}}
                    onClick={handleResend}
                    disabled={auth.otpResending || resendCountdown > 0}
                  >
                    {resendCountdown > 0 ? `Resend (${mm}:${ss})` : 'Resend OTP'}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
      <AuthCommonBanner></AuthCommonBanner>
    </section>
  );
};

export default RegisterNew;
